// Chakra imports
import {
  Flex,
  Grid,
  SimpleGrid,
  useColorModeValue,
  Text,
  Button,
  Box,
  Heading,
  Stack,
} from "@chakra-ui/react";
// Custom icons
import {
  CartIcon,
  DocumentIcon,
  GlobeIcon,
  WalletIcon,
  PersonIcon,
  StatsIcon
} from "components/Icons/Icons.js";
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { getJobPostings, getUserApplications, getCandidatePool } from "utils/firestoreUtils";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import MiniStatistics from "./components/MiniStatistics";

function truncateDescription(text, limit = 15) {
  if (!text) return '';
  return text.length > limit ? `${text.slice(0, limit)}...` : text;
}

export default function Dashboard() {
  const iconBoxInside = useColorModeValue("white", "white");
  const textColor = useColorModeValue("gray.700", "white");
  const { currentUser, userProfile } = useAuth();
  const history = useHistory();

  const [stats, setStats] = useState({
    openJobs: 0,
    myApplications: 0,
    selectedApplications: 0,
    totalCandidates: 0
  });

  const [recentJobs, setRecentJobs] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, [currentUser]);

  async function loadDashboardData() {
    try {
      // 공개 공고 수
      const jobs = await getJobPostings({ openOnly: true });
      setRecentJobs(jobs.slice(0, 5));
      setStats(prev => ({ ...prev, openJobs: jobs.length }));

      // 사용자 신청 현황
      if (currentUser) {
        const applications = await getUserApplications(currentUser.uid);
        const selected = applications.filter(app => app.status === 'selected');
        setStats(prev => ({ 
          ...prev, 
          myApplications: applications.length,
          selectedApplications: selected.length
        }));
      }

      // 관리자: 구인 풀 인원 수
      if (userProfile?.role === 'admin' || userProfile?.role === 'manager') {
        const candidates = await getCandidatePool({ activeOnly: true });
        setStats(prev => ({ ...prev, totalCandidates: candidates.length }));
      }
    } catch (error) {
      console.error('대시보드 데이터 로딩 오류:', error);
    }
  }

  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'manager';

  return (
    <Flex flexDirection='column' pt={{ base: "120px", md: "75px" }}>
      {/* 통계 카드 */}
      <SimpleGrid columns={{ sm: 1, md: 2, xl: 4 }} spacing='24px' mb='26px'>
        <MiniStatistics
          title={"모집중인 공고"}
          amount={stats.openJobs}
          icon={<DocumentIcon h={"24px"} w={"24px"} color={iconBoxInside} />}
        />
        <MiniStatistics
          title={"나의 지원"}
          amount={stats.myApplications}
          icon={<CartIcon h={"24px"} w={"24px"} color={iconBoxInside} />}
        />
        <MiniStatistics
          title={"선정된 공고"}
          amount={stats.selectedApplications}
          icon={<StatsIcon h={"24px"} w={"24px"} color={iconBoxInside} />}
        />
        {isAdmin && (
          <MiniStatistics
            title={"활동 인원"}
            amount={stats.totalCandidates}
            icon={<PersonIcon h={"24px"} w={"24px"} color={iconBoxInside} />}
          />
        )}
      </SimpleGrid>

      <Grid
        templateColumns={{ sm: "1fr", lg: "2fr 1fr" }}
        gap='24px'>
        {/* 최근 공고 */}
        <Card>
          <CardHeader p="6px 0px 22px 0px">
            <Flex justify="space-between" align="center">
              <Text fontSize="xl" color={textColor} fontWeight="bold">
                최근 구인 공고
              </Text>
              <Button 
                size="sm" 
                colorScheme="blue"
                ml={4}
                onClick={() => history.push('/admin/job-postings')}
              >
                전체보기
              </Button>
            </Flex>
          </CardHeader>
          <CardBody>
            <Stack spacing={4}>
              {recentJobs.length === 0 ? (
                <Text color="gray.500" textAlign="center" py={8}>
                  등록된 공고가 없습니다.
                </Text>
              ) : (
                recentJobs.map((job) => (
                  <Box
                    key={job.id}
                    p={4}
                    borderWidth="1px"
                    borderRadius="lg"
                    _hover={{ bg: useColorModeValue('gray.50', 'gray.600'), cursor: 'pointer' }}
                    onClick={() => history.push(`/admin/job-postings/${job.id}`)}
                  >
                    <Heading size="sm" mb={2}>{job.title}</Heading>
                    <Text fontSize="sm" color="gray.500">
                      {truncateDescription(job.description, 20)}
                    </Text>
                    <Flex justify="space-between" mt={2} fontSize="xs" color="gray.500">
                      <Text>{job.location}</Text>
                      <Text>{job.headcount}명 모집</Text>
                    </Flex>
                  </Box>
                ))
              )}
            </Stack>
          </CardBody>
        </Card>

        {/* 빠른 메뉴 */}
        <Card>
          <CardHeader p="6px 0px 22px 0px">
            <Text fontSize="xl" color={textColor} fontWeight="bold">
              빠른 메뉴
            </Text>
          </CardHeader>
          <CardBody>
            <Stack spacing={3}>
              <Button
                w="100%"
                colorScheme="blue"
                onClick={() => history.push('/admin/job-postings')}
              >
                구인 공고 보기
              </Button>
              <Button
                w="100%"
                colorScheme="green"
                onClick={() => history.push('/admin/applications')}
              >
                나의 지원 현황
              </Button>
              <Button
                w="100%"
                colorScheme="purple"
                onClick={() => history.push('/admin/my-profile')}
              >
                프로필 수정
              </Button>
              {isAdmin && (
                <>
                  <Button
                    w="100%"
                    colorScheme="orange"
                    onClick={() => history.push('/admin/job-pool')}
                  >
                    구인 풀 관리
                  </Button>
                  <Button
                    w="100%"
                    colorScheme="teal"
                    onClick={() => history.push('/admin/job-postings/create')}
                  >
                    공고 등록
                  </Button>
                </>
              )}
            </Stack>
          </CardBody>
        </Card>
      </Grid>
    </Flex>
  );
}
